import { getInvoicesList } from 'actions/billing'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import PaymentMethodHistoryDialog from 'features/Billing/components/PaymentMethodStatus/PaymentMethodHistoryDialog'
import PaymentMethodStatusDialog from 'features/Billing/components/PaymentMethodStatus/PaymentMethodStatusDialog'
import usePaymentMethodStatus from 'features/Billing/components/PaymentMethodStatus/usePaymentMethodStatus'
import EditCreditCardDialog from 'features/Billing/MakePayment/EditCreditCardDialog'
import PaymentMethodsDialog from 'features/Billing/MakePayment/PaymentMethodsDialog'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation, useParams } from 'react-router'
import { NavLink } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import billingApi from 'services/api/billing'
import paymentApi from 'services/api/payment'

import {
  selectCurrentBalance,
  selectCurrentGroupName,
  selectCurrentHotelName,
  selectIsInternalAdmin
} from '../../../accessors/index'
import AccountActivatedDialog from '../../../components/ReusableComponents/AccountActivatedDialog/index'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import PaymentCompleteDialog from '../../../components/ReusableComponents/PaymentCompleteDialog/index'
import PaymentFailedDialog from '../../../components/ReusableComponents/PaymentFailedDialog/index'
import { getUSDstring } from '../../../utils/helpers'
import IncomingACHCredit from './IncomingACHCredit'
import MakeCreditCardPayment from './MakeActualCreditCardPayment/index'
import MakeCheckPayment from './MakeCheckPayment/index'
import MakeHotelCreditPayment from './MakeHotelCreditPayment/index'
import styles from './MakePayment.module.scss'

export const filterSelectableSavedPaymentMethods = (methods, isInternalAdmin) =>
  isInternalAdmin ? methods : methods.filter(method => method.method !== 'Check')

const Header = ({ type, isPaymentForGroup, hotelName, groupName, balance }) => {
  const relevantParty = isPaymentForGroup ? groupName : hotelName

  return (
    <CustomHeader width={800}>
      <ReactTooltip />
      <div className={styles.headerContainer}>
        <div className={styles.headerContent}>
          {type === 'payment' && (
            <h1>
              Payment for <strong data-tip={relevantParty}>{relevantParty}</strong>
            </h1>
          )}
          {type === 'activation' && <h1>Activate your account</h1>}
          {type === 'addMethod' && <h1>Add payment method</h1>}
          {type === 'payment' && (
            <p>Current balance: {balance ? getUSDstring(balance) : '$0'}</p>
          )}
        </div>
      </div>
    </CustomHeader>
  )
}

const MakePayment = ({
  goBackPath = '/billing',
  showTabs = true,
  isAmountLocked = false,
  isAutoPay = false
}) => {
  const isInitialPaymentMade = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)

  const [isLoading, setLoading] = useState()
  const [isConfirmationVisible, setConfirmationVisible] = useState()
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([])

  const [isAddedPaymentMethod, setAddedPaymentMethod] = useState()
  const [isAccountActivated, setAccountActivated] = useState()
  const [showPaymentFailedDialog, setShowPaymentFailedDialog] = useState({
    status: false,
    message: ''
  })

  const [isPaymentMethodDialogVisible, setIsPaymentMethodDialogVisible] = useState(true)

  const [selectedSavedPaymentMethodId, setSelectedSavedPaymentMethodId] = useState()

  const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(true)

  const [creditCardBeingEdited, setCreditCardBeingEdited] = useState()

  const [isInitialPaymentSuccessful, setIsInitialPaymentSuccessful] = useState(false)
  const [isInitialPaymentFailed, setIsInitialPaymentFailed] = useState({
    isFailed: false,
    message: ''
  })

  const [amount, setAmount] = useState()

  const location = useLocation()

  const rootPath = location.pathname.split('/')[1]

  const { type, paymentType } = useParams()

  const dispatch = useDispatch()

  const { addToast } = useToasts()

  useEffect(() => {
    setLoading(true)
    dispatch(getInvoicesList()).then(() => setLoading(false))
  }, [dispatch])

  async function handleSubmit(params) {
    setLoading(true)
    const postData = params

    const paymentService = !isInitialPaymentMade
      ? paymentApi.makeInitialPayment
      : paymentApi.makePayment

    paymentService(postData)
      .then(() => {
        if (type === 'addMethod') {
          setAddedPaymentMethod(true)
        }

        if (type === 'payment') {
          setAmount(params.PaymentAmount)
          setConfirmationVisible(true)
        }

        if (type === 'activation') {
          setAccountActivated(true)
        }

        if (!isInitialPaymentMade) {
          setIsInitialPaymentSuccessful(true)
        }
      })
      .catch(error => {
        if (!isInitialPaymentMade) {
          setIsInitialPaymentFailed({
            isFailed: true,
            message: error.message.toString()
          })
        }
        setShowPaymentFailedDialog(prev => ({
          ...prev,
          status: true,
          message:
            error.message.toString() ||
            'Something went wrong while adding this payment method.'
        }))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const balance = useSelector(selectCurrentBalance)
  const hotelName = useSelector(selectCurrentHotelName)
  const groupName = useSelector(selectCurrentGroupName)
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const refreshSavedPaymentMethods = useCallback(
    () => billingApi.getSavedPaymentMethods().then(res => setSavedPaymentMethods(res)),
    []
  )

  const {
    closeHistoryDialog,
    closeStatusDialog,
    confirmStatus,
    historyDialog,
    openHistoryDialog,
    openStatusDialog,
    savingPaymentAccountId,
    statusDialog,
    statusError
  } = usePaymentMethodStatus(refreshSavedPaymentMethods)

  useEffect(() => {
    billingApi.getSavedPaymentMethods().then(res => {
      setSavedPaymentMethods(res)
      setIsPaymentMethodsLoading(false)
    })
  }, [])

  function makePaymentMethodDefault(paymentAccountId) {
    setIsPaymentMethodsLoading(true)

    billingApi
      .makeBillingPaymentDefault({ paymentAccountId })
      .then(() => billingApi.getSavedPaymentMethods())
      .then(res => setSavedPaymentMethods(res))
      .then(() => setIsPaymentMethodsLoading(false))
  }

  function deletePaymentMethod(paymentAccountId) {
    setIsPaymentMethodsLoading(true)

    billingApi
      .deletePaymentMethod({ paymentAccountId })
      .then(() => billingApi.getSavedPaymentMethods())
      .then(res => setSavedPaymentMethods(res))
      .then(() => setIsPaymentMethodsLoading(false))
  }

  function updatePaymentMethod({ cvv, expirationMonth, expirationYear }) {
    setIsPaymentMethodsLoading(true)

    billingApi
      .updatePaymentMethod({
        cvv,
        expirationMonth,
        expirationYear,
        paymentAccountId: creditCardBeingEdited.paymentAccountId
      })
      .then(() => {
        addToast(`Credit card has updated succesfully`, {
          appearance: 'success',
          autoDismiss: true
        })
      })
      .catch(() => {
        addToast(`Something went wrong while updating the credit card`, {
          appearance: 'error',
          autoDismiss: true
        })
        setIsPaymentMethodsLoading(false)
        setCreditCardBeingEdited(null)
      })
      .then(() => billingApi.getSavedPaymentMethods())
      .then(res => setSavedPaymentMethods(res))
      .then(() => {
        setIsPaymentMethodsLoading(false)
        setCreditCardBeingEdited(null)
      })
  }

  const hasOwnerGroup = groupName !== 'Independently Managed Hotels'

  function selectPaymentMethod(paymentAccountId) {
    setSelectedSavedPaymentMethodId(paymentAccountId)
    setIsPaymentMethodDialogVisible(false)
  }

  const selectableSavedPaymentMethods = filterSelectableSavedPaymentMethods(
    savedPaymentMethods,
    isInternalAdmin
  )

  if (isInitialPaymentSuccessful) {
    return (
      <ConfirmationDialog
        title="Your payment was successful"
        positiveMessage="Start using the Directful Dashboard"
        hasCloseButton={false}
        onConfirm={() => {
          window.location.reload()
        }}
      >
        <p>Thank you for your payment. Hotel setup is now complete.</p>
      </ConfirmationDialog>
    )
  }

  if (isInitialPaymentFailed.isFailed) {
    return (
      <ConfirmationDialog
        title="Your payment was not successful"
        positiveMessage="Try again"
        hasCloseButton={false}
        onConfirm={() => {
          window.location.reload()
        }}
      >
        <p>{isInitialPaymentFailed.message}</p>
      </ConfirmationDialog>
    )
  }

  if (statusDialog) {
    return (
      <PaymentMethodStatusDialog
        error={statusError}
        isDisabling={statusDialog.isDisabling}
        isSaving={savingPaymentAccountId === statusDialog.paymentAccountId}
        onClose={closeStatusDialog}
        onConfirm={confirmStatus}
      />
    )
  }

  if (historyDialog) {
    return (
      <PaymentMethodHistoryDialog
        error={historyDialog.error}
        history={historyDialog.history}
        isLoading={historyDialog.isLoading}
        onClose={closeHistoryDialog}
      />
    )
  }

  if (isLoading || isPaymentMethodsLoading) {
    return <LoadingIndicator />
  }

  if (creditCardBeingEdited) {
    return (
      <EditCreditCardDialog
        onClose={() => setCreditCardBeingEdited(null)}
        creditCardInfo={creditCardBeingEdited}
        onSubmit={updatePaymentMethod}
      />
    )
  }

  if (
    isPaymentMethodDialogVisible &&
    selectableSavedPaymentMethods.length > 0 &&
    type !== 'addMethod'
  ) {
    return (
      <PaymentMethodsDialog
        savedPaymentMethods={selectableSavedPaymentMethods}
        onSelect={selectPaymentMethod}
        onClose={() => setIsPaymentMethodDialogVisible(false)}
        makeMethodDefault={makePaymentMethodDefault}
        deleteMethod={deletePaymentMethod}
        setCardIdToEdit={paymentAccountId =>
          setCreditCardBeingEdited(
            selectableSavedPaymentMethods.find(m => m.paymentAccountId === paymentAccountId)
          )
        }
        savingPaymentAccountId={savingPaymentAccountId}
        openStatusDialog={openStatusDialog}
        openHistoryDialog={openHistoryDialog}
      />
    )
  }

  const selectedPaymentMethodType = selectableSavedPaymentMethods.find(
    method => method.paymentAccountId === selectedSavedPaymentMethodId
  )?.method

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {isConfirmationVisible && (
          <PaymentCompleteDialog
            amount={amount}
            isProcessed={paymentType === 'credit-card'}
          />
        )}
        {isAddedPaymentMethod && <Redirect to={`/${rootPath}/editBillingInfo`} />}
        {isAccountActivated && <AccountActivatedDialog />}
        {showPaymentFailedDialog.status && (
          <PaymentFailedDialog
            setShowPaymentFailedDialog={setShowPaymentFailedDialog}
            message={showPaymentFailedDialog.message || ''}
            rootPath={rootPath}
          />
        )}
        <Header
          hotelName={hotelName}
          groupName={groupName}
          type={type}
          hasOwnerGroup={hasOwnerGroup}
          amount={amount}
          balance={balance}
        />
        {showTabs && (
          <div className={styles.tabContainer}>
            {selectedPaymentMethodType ? (
              selectedPaymentMethodType === 'CreditCard' ? (
                <Redirect to={`/${rootPath}/${type}/credit-card`} />
              ) : (
                <Redirect to={`/${rootPath}/${type}/check`} />
              )
            ) : null}
            <NavLink
              to={`/${rootPath}/${type}/check`}
              className={styles.tab}
              activeClassName={styles.activeTab}
            >
              Check
            </NavLink>
            <NavLink
              to={`/${rootPath}/${type}/credit-card`}
              className={styles.tab}
              activeClassName={styles.activeTab}
            >
              Credit card
            </NavLink>
            {isInternalAdmin && type === 'payment' && (
              <NavLink
                to={`/${rootPath}/${type}/hotel-credit`}
                className={styles.tab}
                activeClassName={styles.activeTab}
              >
                Hotel credit
              </NavLink>
            )}
            {isInternalAdmin && type === 'payment' && (
              <NavLink
                to={`/${rootPath}/${type}/ach`}
                className={styles.tab}
                activeClassName={styles.activeTab}
              >
                Incoming ACH
              </NavLink>
            )}
          </div>
        )}
        <Switch>
          <Route
            path={`/${rootPath}/:type/check`}
            render={() => (
              <MakeCheckPayment
                handleSubmit={handleSubmit}
                savedPaymentMethods={savedPaymentMethods}
                selectedSavedPaymentMethodId={selectedSavedPaymentMethodId}
                goBackPath={goBackPath}
                isAmountLocked={isAmountLocked}
                isInitialPaymentMade={isInitialPaymentMade}
              />
            )}
          />
          <Route
            path={`/${rootPath}/:type/credit-card`}
            render={() => (
              <MakeCreditCardPayment
                handleSubmit={handleSubmit}
                savedPaymentMethods={savedPaymentMethods}
                selectedSavedPaymentMethodId={selectedSavedPaymentMethodId}
                goBackPath={goBackPath}
                isAmountLocked={isAmountLocked}
                isAutoPay={isAutoPay}
              />
            )}
          />
          <Route
            path={`/${rootPath}/:type/hotel-credit`}
            goBackPath={goBackPath}
            render={() => (
              <MakeHotelCreditPayment
                handleSubmit={handleSubmit}
                goBackPath={goBackPath}
              />
            )}
          />
          <Route
            path={`/${rootPath}/:type/ach`}
            goBackPath={goBackPath}
            render={() => (
              <IncomingACHCredit handleSubmit={handleSubmit} goBackPath={goBackPath} />
            )}
          />
        </Switch>
      </div>
    </div>
  )
}
export default MakePayment
